import React from 'react';
import {Text, TextPriority} from '@wix/wix-ui-tpa/cssVars';
import TagSmall from 'wix-ui-icons-common/on-stage/TagSmall';
import {useControllerProps} from '../../ControllerContext';
import {BuyerNoteSection} from './BuyerNoteSection/BuyerNoteSection';
import * as s from './TotalsSection.scss';
import {classes} from './TotalsSectionOverride.st.css';
import settingsParams from '../../../settingsParams';
import {Divider} from '@wix/wix-ui-tpa';
import {useLocaleKeys} from '../../../LocaleKeysProvider';
import {shorten} from './utils';
import {shouldShowShipping} from '../Layout/utils';
import {useSettingsWithDefaults} from '../../../../../hooks/useSettingsWithDefaults';
import {Totals} from './Subtotals/Totals';
import {Subscriptions} from './Subscriptions/Subscriptions';
import {useTaxBreakdownLines} from './useTaxBreakdownLines';
import {useCurrencyFormatter} from '../../../../../hooks/useCurrencyFormatter';
import {useOrderDiscounts} from '../../hooks/useOrderDiscounts';

export enum TotalsSectionDataHook {
  root = 'TotalsSectionDataHook.root',
  buyerNoteWrapper = 'TotalsSectionDataHook.buyerNoteWrapper',
  totalsWrapper = 'TotalsSectionDataHook.totalsWrapper',
  paymentStatus = 'TotalsSectionDataHook.paymentStatus',
  offlinePaymentStatus = 'TotalsSectionDataHook.offlinePaymentStatus',
  paid = 'TotalsSectionDataHook.offlinePaymentPaid',
  offlinePaymentDueLater = 'TotalsSectionDataHook.offlinePaymentDueLater',
  buyerNoteLabel = 'TotalsSectionDataHook.buyerNoteLabel',
  buyerNote = 'TotalsSectionDataHook.buyerNote',
  additionalFeesRoot = 'TotalsSectionDataHook.additionalFeesRoot',
  afterFreeTrial = 'TotalsSectionDataHook.afterFreeTrial',
  totalPrice = 'TotalsSectionDataHook.TotalPrice',
  delayedPayments = 'TotalsSectionDataHook.delayedPayments',
  subscriptionProjection = 'TotalsSectionDataHook.subscriptionProjection',
  subscriptionProjectionToggle = 'TotalsSectionDataHook.subscriptionProjectionToggle',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function TotalsSection() {
  const localeKeys = useLocaleKeys();
  const formatCurrency = useCurrencyFormatter();
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const {
    isOfflinePayment,
    formattedTotalsBalance,
    formattedTotalsPaid,
    totalsPaid,
    formattedTaxPrice,
    formattedShippingPrice,
    formattedSubTotalPrice,
    formattedTotalPrice,
    formattedDiscount,
    couponCode,
    isPickupSelected,
    shippingPrice,
    formattedTotalGiftCardAmount,
    hasGiftCard,
    totalGiftCardAmount,
    taxName,
    address: shippingAddress,
    pickupAddress,
    shouldDisplayAdditionalFees,
    shouldDisplayPlatformFees,
    additionalFees,
    platformFees,
    formattedPayAfterFreeTrialPrice,
    payAfterFreeTrialPrice,
    supportFreeTrialTYP,
    subscriptionProjection,
    shouldShowMultipleSubscriptions,
    shouldEnableTaxExemptions,
    isItemSubtotalDiscountInclusive,
    supportMultipleGCInTYP,
  } = useControllerProps().thankYouPageStore;
  const taxLines = useTaxBreakdownLines();
  const {globalDiscounts, shippingDiscounts} = useOrderDiscounts();

  const showShipping = shouldShowShipping({isPickupSelected, pickupAddress, shippingAddress});
  const shouldDisplayAfterFreeTrial = Boolean(payAfterFreeTrialPrice > 0 && supportFreeTrialTYP);
  const shouldHidePaid = shouldDisplayAfterFreeTrial && totalsPaid === 0;

  const getTotalTitle = () => {
    return (
      // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
      getSettingsWithDefaults(settingsParams.THANK_YOU_PAGE_TOTAL_COST_LABEL)
    );
  };

  const getShippingTitle = () => {
    return isPickupSelected ? localeKeys.thankYouPage.pickup.label() : localeKeys.thankYouPage.shipping.label();
  };

  const getShippingValue = () => {
    return shippingPrice === 0 ? localeKeys.thankYouPage.deliveryMethod.free.label() : formattedShippingPrice;
  };

  const getAdditionalFees: Function = (): JSX.Element[] => {
    return additionalFees.map((additionalFee) => (
      <Totals.SubtotalRow
        key={additionalFee.code}
        title={additionalFee.name}
        // @ts-expect-error: titk@wix.com - Suppress for regression on making TS config stricter. Fix please if you touch this code 🙏
        value={additionalFee.convertedFormattedTotalPriceBeforeTax}
        className={classes.mySubtotalRow}
        dataHook={TotalsSectionDataHook.additionalFeesRoot}
      />
    ));
  };

  const getTaxBreakdown: Function = (): JSX.Element[] => {
    return taxLines.map(({title, value}, index) => (
      <Totals.SubtotalRow key={index} title={title} value={value} className={classes.mySubtotalRow} />
    ));
  };

  return (
    <div className={s.root} data-hook={TotalsSectionDataHook.root}>
      <BuyerNoteSection />
      {
        <div className={s.totals}>
          <div className={s.section} data-hook={TotalsSectionDataHook.totalsWrapper}>
            <Totals>
              <Totals.SubtotalRow
                title={localeKeys.thankYouPage.subtotal.label()}
                value={formattedSubTotalPrice}
                className={classes.mySubtotalRow}
              />
              {isItemSubtotalDiscountInclusive &&
                globalDiscounts.map((discount, index) => (
                  <Totals.SubtotalRow
                    key={`global-discount-${index}`}
                    title={
                      <span className={classes.discountTitle}>
                        <TagSmall className={classes.discountIcon} />
                        {discount.label}
                      </span>
                    }
                    value={`${formatCurrency(-discount.amount)}`}
                    className={classes.mySubtotalRow}
                  />
                ))}
              {shouldDisplayAdditionalFees && getAdditionalFees()}
              {showShipping && (
                <Totals.SubtotalRow
                  title={getShippingTitle()}
                  value={getShippingValue()}
                  className={classes.mySubtotalRow}
                />
              )}
              {isItemSubtotalDiscountInclusive &&
                shippingDiscounts.map((discount, index) => (
                  <Totals.SubtotalRow
                    key={`free-shipping-discount-${index}`}
                    title={
                      <span className={classes.discountTitle}>
                        <TagSmall className={classes.discountIcon} />
                        {discount.label}
                      </span>
                    }
                    value={`${formatCurrency(-discount.amount)}`}
                    className={classes.mySubtotalRow}
                  />
                ))}
              {shouldEnableTaxExemptions ? (
                getTaxBreakdown()
              ) : (
                <Totals.SubtotalRow
                  title={taxName || /* istanbul ignore next */ localeKeys.thankYouPage.tax.label()}
                  value={formattedTaxPrice}
                  className={classes.mySubtotalRow}
                />
              )}
              {shouldDisplayPlatformFees &&
                platformFees.map((platformFee) => (
                  <Totals.SubtotalRow
                    key={platformFee.code}
                    title={platformFee.name ?? /* istanbul ignore next */ ''}
                    value={platformFee.convertedFormattedTotalPriceBeforeTax ?? /* istanbul ignore next */ ''}
                    className={classes.mySubtotalRow}
                  />
                ))}
              {!isItemSubtotalDiscountInclusive && !!couponCode && (
                <Totals.SubtotalRow
                  title={localeKeys.thankYouPage.promoCode.label({couponCode: shorten(couponCode, 10, '...')})}
                  value={`-${formattedDiscount}`}
                  className={classes.mySubtotalRow}
                />
              )}
              {!!hasGiftCard && !!totalGiftCardAmount && totalGiftCardAmount > 0 && (
                <Totals.SubtotalRow
                  title={
                    supportMultipleGCInTYP
                      ? localeKeys.thankYouPage.giftCards.label()
                      : localeKeys.thankYouPage.giftCard.label()
                  }
                  value={`-${formattedTotalGiftCardAmount}`}
                  className={classes.mySubtotalRow}
                />
              )}
            </Totals>
            <Divider className={s.totalRowDivider} />
            <Totals>
              <Totals.TotalRow
                dataHook={TotalsSectionDataHook.totalPrice}
                className={classes.myTotalRow}
                title={getTotalTitle()}
                value={formattedTotalPrice}
              />
            </Totals>
            {(shouldDisplayAfterFreeTrial || isOfflinePayment) && (
              <div data-hook={TotalsSectionDataHook.delayedPayments}>
                <Divider className={s.divider} />
                <Totals>
                  {!shouldHidePaid && (
                    <Totals.TotalRow
                      className={classes.myTotalRow}
                      title={localeKeys.thankYouPage.paid.label()}
                      value={formattedTotalsPaid}
                      dataHook={TotalsSectionDataHook.paid}
                    />
                  )}
                  {shouldDisplayAfterFreeTrial && (
                    <Totals.SubtotalRow
                      className={classes.mySubTotal}
                      title={localeKeys.thankYouPage.pricingPlans.afterFreeTrial.label()}
                      value={formattedPayAfterFreeTrialPrice}
                      dataHook={TotalsSectionDataHook.afterFreeTrial}
                    />
                  )}
                  {isOfflinePayment && (
                    <Totals.SubtotalRow
                      title={localeKeys.thankYouPage.dueLater.label()}
                      value={formattedTotalsBalance}
                      className={classes.mySubtotalRow}
                      dataHook={TotalsSectionDataHook.offlinePaymentDueLater}
                    />
                  )}
                </Totals>
              </div>
            )}
          </div>
          {shouldShowMultipleSubscriptions && <Subscriptions />}
          {subscriptionProjection && !shouldShowMultipleSubscriptions && (
            <Text
              priority={TextPriority.secondary}
              className={classes.projectionText}
              data-hook={TotalsSectionDataHook.subscriptionProjection}>
              {subscriptionProjection}
            </Text>
          )}
        </div>
      }
    </div>
  );
}
